import { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { Spin } from 'antd';
import axios from 'axios';
import './App.css';

import { RouteTracker } from './utils/appInsights';
import Authentication from './pages/Authentication';
import AccessDeniedPage from './pages/AccessDeniedPage';
import ProtectedRoute from './components/ProtectedRoute';
import { getGuestToken } from './actions/user';
import { requireAccessCode } from './utils/envConfig';

const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatListPage = lazy(() => import('./pages/ChatListPage'));
const AdminHistoryPage = lazy(() => import('./pages/AdminHistoryPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));

const LOADING_FALLBACK = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

const chatRoutes: { path: string; element: React.ReactElement }[] = [
  { path: '/chat',      element: <ChatPage /> },
  { path: '/chat/:id',  element: <ChatPage /> },
  { path: '/chat-list', element: <ChatListPage /> },
];

const adminRoutes: { path: string; element: React.ReactElement }[] = [
  { path: '/admin/history', element: <AdminHistoryPage /> },
  { path: '/admin/feedback', element: <FeedbackPage /> },
];

function App() {
  // When access code is required, there's nothing to bootstrap — render immediately,
  // same as before this feature existed. Only the open-chat mode needs a guest token
  // in hand before routes render.
  const [authReady, setAuthReady] = useState(requireAccessCode);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          sessionStorage.removeItem('token');
          window.location.replace('/login');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    if (requireAccessCode) return;
    (async () => {
      if (!sessionStorage.getItem('token')) {
        await getGuestToken();
      }
      setAuthReady(true);
    })();
  }, []);

  if (!authReady) {
    return LOADING_FALLBACK;
  }

  return (
    <BrowserRouter>
      <RouteTracker />
      <div className="App">
        <Suspense fallback={LOADING_FALLBACK}>
          <Routes>
            <Route index element={<Navigate to={requireAccessCode ? "/login" : "/chat"} replace />} />
            <Route path="/login" element={<Authentication />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            {chatRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={requireAccessCode ? <ProtectedRoute>{element}</ProtectedRoute> : element}
              />
            ))}
            {adminRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={<ProtectedRoute adminOnly>{element}</ProtectedRoute>} />
            ))}
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  );
}

export default App;
